import { useEffect, useRef, useState } from "react";
import makeBallSprite from "./ballSprite";

import "./game.scss";

export default function VolleyGame() {
  const canvasRef = useRef(null);
  const worldRef = useRef(null);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem("vbl-rally-best") || 0));
  const [mode, setMode] = useState("ready");
  const modeRef = useRef("ready");

  const changeMode = (next) => {
    modeRef.current = next;
    setMode(next);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const sprite = makeBallSprite(256);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf;
    let last = performance.now();

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const w = worldRef.current;
      if (w) {
        w.w = rect.width;
        w.h = rect.height;
      }
    };

    const rect = canvas.parentElement.getBoundingClientRect();
    worldRef.current = {
      w: rect.width,
      h: rect.height,
      x: rect.width / 2,
      y: rect.height * 0.4,
      vx: 0,
      vy: 0,
      angle: 0,
      va: 0.6,
      r: 44,
      score: 0,
      t: 0,
    };
    resize();
    window.addEventListener("resize", resize);

    const loop = (now) => {
      const w = worldRef.current;
      const dt = Math.min(0.033, (now - last) / 1000);
      last = now;
      w.t += dt;

      if (modeRef.current === "playing") {
        w.vy += 1350 * dt;
        w.x += w.vx * dt;
        w.y += w.vy * dt;
        w.angle += w.va * dt;
        if (w.x < w.r) {
          w.x = w.r;
          w.vx = Math.abs(w.vx) * 0.84;
        }
        if (w.x > w.w - w.r) {
          w.x = w.w - w.r;
          w.vx = -Math.abs(w.vx) * 0.84;
        }
        if (w.y < w.r) {
          w.y = w.r;
          w.vy = Math.abs(w.vy) * 0.7;
        }
        if (w.y - w.r > w.h) {
          changeMode("over");
          setBest((prev) => {
            const next = Math.max(prev, w.score);
            localStorage.setItem("vbl-rally-best", String(next));
            return next;
          });
        }
      } else {
        w.y = w.h * 0.4 + Math.sin(w.t * 1.6) * 14;
        w.angle += 0.4 * dt;
      }

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w.w, w.h);
      ctx.save();
      ctx.translate(w.x, w.y);
      ctx.rotate(w.angle);
      ctx.drawImage(sprite, -w.r, -w.r, w.r * 2, w.r * 2);
      ctx.restore();

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const onPointer = (e) => {
      const w = worldRef.current;
      const box = canvas.getBoundingClientRect();
      const px = e.clientX - box.left;
      const py = e.clientY - box.top;
      const dist = Math.hypot(px - w.x, py - w.y);
      if (dist > w.r + 46) return;

      if (modeRef.current !== "playing") {
        w.score = 0;
        setScore(0);
        w.x = Math.min(Math.max(px, w.r), w.w - w.r);
        w.y = w.h * 0.4;
        changeMode("playing");
      } else {
        w.score += 1;
        setScore(w.score);
      }
      w.vy = -(700 + Math.min(w.score * 6, 220));
      w.vx = Math.min(Math.max((w.x - px) * 9, -460), 460);
      w.va = (Math.random() - 0.5) * 7;
    };
    canvas.addEventListener("pointerdown", onPointer);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("pointerdown", onPointer);
    };
  }, []);

  return (
    <div className="volley-game">
      <canvas ref={canvasRef}></canvas>
      <div className="hud">
        <div className="hud-readout">
          <span className="hud-value">{String(score).padStart(2, "0")}</span>
          <span className="label">Rally</span>
        </div>
        <div className="hud-readout">
          <span className="hud-value hud-best">{String(best).padStart(2, "0")}</span>
          <span className="label">Best</span>
        </div>
      </div>
      {mode !== "playing" && (
        <div className="overlay">
          <p className="overlay-title">{mode === "over" ? `Out. Rally of ${score}.` : "Ready to serve?"}</p>
          <p className="overlay-hint">Click the ball to {mode === "over" ? "go again" : "start"}. Keep it off the floor.</p>
        </div>
      )}
    </div>
  );
}
